/**
 * UTM 파라미터 관리 유틸리티
 * GA 페이지뷰 추적을 위한 utm_source 파라미터 처리
 */

// UTM 파라미터 타입 정의
export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

// sessionStorage 키
const UTM_STORAGE_KEY = 'utm_params';

/**
 * URL에서 UTM 파라미터 추출
 * @param url URL 문자열 또는 URLSearchParams 객체
 * @returns UTM 파라미터 객체
 */
export const extractUTMParams = (url: string | URLSearchParams): UTMParams => {
  try {
    const params = typeof url === 'string' ? new URLSearchParams(url) : url;
    const utmParams: UTMParams = {};

    // utm_source가 있는 경우에만 처리 (현재 주요 관심사)
    const utmSource = params.get('utm_source');
    if (utmSource) {
      utmParams.utm_source = utmSource;
      
      // 다른 UTM 파라미터들도 함께 저장 (향후 확장성)
      const utmMedium = params.get('utm_medium');
      const utmCampaign = params.get('utm_campaign');
      const utmTerm = params.get('utm_term');
      const utmContent = params.get('utm_content');
      
      if (utmMedium) utmParams.utm_medium = utmMedium;
      if (utmCampaign) utmParams.utm_campaign = utmCampaign;
      if (utmTerm) utmParams.utm_term = utmTerm;
      if (utmContent) utmParams.utm_content = utmContent;
    }

    return utmParams;
  } catch (error) {
    console.warn('UTM 파라미터 추출 실패:', error);
    return {};
  }
};

/**
 * UTM 파라미터를 sessionStorage에 저장
 * @param utmParams UTM 파라미터 객체
 */
export const saveUTMParams = (utmParams: UTMParams): void => {
  try {
    if (Object.keys(utmParams).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
      
      // 개발 환경에서만 로그 출력
      if (process.env.NODE_ENV === 'development') {
        console.log('UTM 파라미터 저장됨:', utmParams);
      }
    }
  } catch (error) {
    console.warn('UTM 파라미터 저장 실패:', error);
  }
};

/**
 * sessionStorage에서 UTM 파라미터 조회
 * @returns 저장된 UTM 파라미터 객체 또는 빈 객체
 */
export const getUTMParams = (): UTMParams => {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('UTM 파라미터 조회 실패:', error);
  }
  return {};
};

/**
 * sessionStorage에서 UTM 파라미터 삭제
 */
export const clearUTMParams = (): void => {
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
    
    // 개발 환경에서만 로그 출력
    if (process.env.NODE_ENV === 'development') {
      console.log('UTM 파라미터 삭제됨');
    }
  } catch (error) {
    console.warn('UTM 파라미터 삭제 실패:', error);
  }
};

/**
 * URLSearchParams에서 UTM 파라미터 제거
 * @param params URLSearchParams 객체
 * @returns UTM 파라미터가 제거되었는지 여부
 */
export const removeUTMFromParams = (params: URLSearchParams): boolean => {
  let hasUTM = false;
  
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  utmKeys.forEach(key => {
    if (params.has(key)) {
      params.delete(key);
      hasUTM = true;
    }
  });
  
  return hasUTM;
};

/**
 * 개발자 디버깅 도구 - 전역 함수로 등록
 */
export const setupUTMDebugTools = (): void => {
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    // 현재 저장된 UTM 정보 확인
    (window as any).debugUTM = () => {
      const utmParams = getUTMParams();
      console.log('현재 저장된 UTM 파라미터:', utmParams);
      return utmParams;
    };
    
    // UTM 정보 초기화
    (window as any).clearUTM = () => {
      clearUTMParams();
      console.log('UTM 파라미터가 초기화되었습니다.');
    };
    
    console.log('UTM 디버깅 도구가 등록되었습니다:');
    console.log('- window.debugUTM(): 현재 UTM 정보 확인');
    console.log('- window.clearUTM(): UTM 정보 초기화');
  }
};
